import { useCallback, useEffect, useRef, useState } from "react";
import preventiveMaintenanceService from "../api/preventiveMaintenance.service";
import { useAuth } from "../../../../contexts/AuthContext";
import {
  CreateInHouseRepairPayload, MaintenanceNotification, Pagination, PreventiveMaintenance,
  PreventiveMaintenanceAnalytics, PreventiveMaintenanceFilters,
} from "../types";

const DEFAULT_PAGINATION: Pagination = { page: 1, limit: 10, total: 0, totalPages: 0 };

export const usePreventiveMaintenance = () => {
  const { user } = useAuth();
  const [preventiveMaintenances, setPreventiveMaintenances] = useState<PreventiveMaintenance[]>([]);
  const [analytics, setAnalytics] = useState<PreventiveMaintenanceAnalytics | null>(null);
  const [notifications, setNotifications] = useState<MaintenanceNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncWarning, setSyncWarning] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>(DEFAULT_PAGINATION);
  const [filters, setFilters] = useState<PreventiveMaintenanceFilters>({ page: 1, limit: 10 });
  const requestVersion = useRef(0);
  const inFlight = useRef(false);
  const notificationReadStorageKey = `spare-parts-preventive-alerts-read:${user?.employeeId || user?.id || "anonymous"}`;
  const [readNotificationIds, setReadNotificationIds] = useState<string[]>([]);

  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(notificationReadStorageKey) || "[]");
      setReadNotificationIds(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : []);
    } catch { setReadNotificationIds([]); }
  }, [notificationReadStorageKey]);

  const refresh = useCallback(async (silent = false, force = false) => {
    const version = ++requestVersion.current;
    inFlight.current = true;
    if (!silent) setLoading(true);
    try {
      const [response, summary] = await Promise.all([
        preventiveMaintenanceService.getPreventiveMaintenances(filters, force),
        preventiveMaintenanceService.getSummary(force),
      ]);
      if (version !== requestVersion.current) return;
      setPreventiveMaintenances(response.items);
      setPagination(response.pagination || DEFAULT_PAGINATION);
      setAnalytics(summary.analytics);
      setNotifications(summary.notifications);
      setSyncWarning(summary.sync?.status === "delayed" ? summary.sync.message || "Vehicle synchronization is delayed." : null);
      setError(null);
    } catch (requestError: any) {
      if (version === requestVersion.current) {
        setError(requestError?.response?.data?.message || "Unable to load preventive schedules. Please retry.");
      }
    } finally {
      if (version === requestVersion.current) { inFlight.current = false; setLoading(false); }
    }
  }, [filters]);

  useEffect(() => {
    void refresh();
    const refreshVisible = () => {
      if (document.visibilityState === "visible" && !inFlight.current) void refresh(true);
    };
    const timer = window.setInterval(refreshVisible, 60_000);
    window.addEventListener("focus", refreshVisible);
    return () => {
      requestVersion.current += 1;
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshVisible);
    };
  }, [refresh]);

  const createInHouseRepair = async (id: string, payload: CreateInHouseRepairPayload) => {
    const result = await preventiveMaintenanceService.createInHouseRepair(id, payload);
    await refresh();
    return result;
  };
  const markNotificationAsRead = async (id: string) => {
    setReadNotificationIds((current) => {
      const next = [...new Set([...current, id])].slice(-500);
      try { localStorage.setItem(notificationReadStorageKey, JSON.stringify(next)); } catch { /* In-memory read state remains available. */ }
      return next;
    });
  };
  const updateFilters = (next: Partial<PreventiveMaintenanceFilters>) => {
    setFilters((current) => ({ ...current, ...next, page: next.page ?? 1 }));
  };
  return {
    preventiveMaintenances, analytics,
    notifications: notifications.map((notification) => ({ ...notification, isRead: readNotificationIds.includes(notification.id) })),
    loading, error, syncWarning, pagination, filters, refresh, createInHouseRepair, markNotificationAsRead, updateFilters,
  };
};

export default usePreventiveMaintenance;
